/**
 * Servicio de anonimización de datos personales (PII).
 * Detecta y reemplaza información identificable en textos para cumplir
 * con LOPDGDD / RGPD en el modo funcionarios.
 */

export interface AnonymizationReport {
  dniCount: number;
  nieCount: number;
  emailCount: number;
  phoneCount: number;
  creditCardCount: number;
  ipAddressCount: number;
  addressCount: number;
  dateOfBirthCount: number;
  totalReplacements: number;
}

export interface AnonymizationResult {
  text: string;
  report: AnonymizationReport;
}

const DNI_REGEX = /\b\d{8}[A-HJ-NP-TV-Z]\b/gi;
const NIE_REGEX = /\b[XYZ]\d{7}[A-HJ-NP-TV-Z]\b/gi;
const EMAIL_REGEX = /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g;
const PHONE_REGEX = /\b(?:\+34[\s\-]?)?(?:6|7|9)\d{2}(?:[\s\-]?\d{3}[\s\-]?\d{3}|\d{2}[\s\-]?\d{2}[\s\-]?\d{2})\b/g;
const CREDIT_CARD_REGEX = /\b(?:4\d{3}|5[1-5]\d{2}|3[47]\d{2}|6(?:011|5\d{2}))[\s\-]?\d{4}[\s\-]?\d{4}[\s\-]?\d{4}\b/g;
const IPV4_REGEX = /\b(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\b/g;
const ADDRESS_REGEX = /\b(?:C\/|Calle|Avenida|Avda?\.?|Plaza|Paseo|Carrer)\s+[A-ZÁÉÍÓÚÑa-záéíóúñ0-9\s,.\-]+(?:\d{5})?\b/gi;
const DOB_REGEX = /\b(?:(?:0?[1-9]|[12]\d|3[01])[\/\-](?:0?[1-9]|1[0-2])[\/\-](?:19|20)\d{2}|(?:19|20)\d{2}[\/\-](?:0?[1-9]|1[0-2])[\/\-](?:0?[1-9]|[12]\d|3[01]))\b/g;

const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

function validateDNI(dni: string): boolean {
  const numPart = parseInt(dni.substring(0, 8), 10);
  const letter = dni.charAt(8).toUpperCase();
  if (isNaN(numPart)) return false;
  return DNI_LETTERS[numPart % 23] === letter;
}

function validateNIE(nie: string): boolean {
  const first = nie.charAt(0).toUpperCase();
  let numStr = '';
  if (first === 'X') numStr = '0' + nie.substring(1, 8);
  else if (first === 'Y') numStr = '1' + nie.substring(1, 8);
  else if (first === 'Z') numStr = '2' + nie.substring(1, 8);
  else return false;
  const num = parseInt(numStr, 10);
  if (isNaN(num)) return false;
  return DNI_LETTERS[num % 23] === nie.charAt(8).toUpperCase();
}

export function anonymizeText(text: string): AnonymizationResult {
  const report: AnonymizationReport = {
    dniCount: 0,
    nieCount: 0,
    emailCount: 0,
    phoneCount: 0,
    creditCardCount: 0,
    ipAddressCount: 0,
    addressCount: 0,
    dateOfBirthCount: 0,
    totalReplacements: 0,
  };

  let result = text;

  result = result.replace(DNI_REGEX, (match) => {
    if (validateDNI(match)) {
      report.dniCount++;
      report.totalReplacements++;
      return '[DNI_ANONIMIZADO]';
    }
    return match;
  });

  result = result.replace(NIE_REGEX, (match) => {
    if (validateNIE(match)) {
      report.nieCount++;
      report.totalReplacements++;
      return '[NIE_ANONIMIZADO]';
    }
    return match;
  });

  result = result.replace(EMAIL_REGEX, () => {
    report.emailCount++;
    report.totalReplacements++;
    return '[EMAIL_ANONIMIZADO]';
  });

  result = result.replace(PHONE_REGEX, (match) => {
    const digits = match.replace(/\D/g, '');
    if (digits.length >= 9) {
      report.phoneCount++;
      report.totalReplacements++;
      return '[TELEFONO_ANONIMIZADO]';
    }
    return match;
  });

  result = result.replace(CREDIT_CARD_REGEX, () => {
    report.creditCardCount++;
    report.totalReplacements++;
    return '[TARJETA_ANONIMIZADA]';
  });

  result = result.replace(IPV4_REGEX, () => {
    report.ipAddressCount++;
    report.totalReplacements++;
    return '[IP_ANONIMIZADA]';
  });

  result = result.replace(ADDRESS_REGEX, () => {
    report.addressCount++;
    report.totalReplacements++;
    return '[DIRECCION_ANONIMIZADA]';
  });

  result = result.replace(DOB_REGEX, () => {
    report.dateOfBirthCount++;
    report.totalReplacements++;
    return '[FECHA_ANONIMIZADA]';
  });

  return { text: result, report };
}

export function hasPII(text: string): boolean {
  return anonymizeText(text).report.totalReplacements > 0;
}

export function getAnonymizationReport(text: string): AnonymizationReport {
  return anonymizeText(text).report;
}

export function anonymizeDocumentContent(content: string): string {
  if (!content || content.length === 0) return content;
  return anonymizeText(content).text;
}
